import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Database } from '../lib/database.types';
import { invalidateUserScores } from '../lib/score-cache-service';
import toast from 'react-hot-toast';

type Profile = Database['public']['Tables']['profiles']['Row'] & {
  pastPerformance?: Array<{
    id: string;
    agency: string;
    agency_selection?: {
      department: string;
      agency?: string;
      bureau?: string;
      fullPath: string;
    };
    project_name: string;
    sector_type: string;
    contract_value: number;
    contract_type: string;
    deliverables: string;
  }>;
};

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string, captchaToken: string) => Promise<void>;
  signUp: (email: string, password: string, companyName: string, role: Profile['role'], captchaToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  forceSignOut: () => void;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to clear only auth-related storage (preserves app caches like score cache)
function clearAuthStorage() {
  try {
    // Clear only supabase auth items from localStorage
    const authKeys = Object.keys(localStorage).filter(key =>
      key.startsWith('supabase.auth.') ||
      key.startsWith('sb-') ||
      key.includes('-auth-token')
    );
    authKeys.forEach(key => localStorage.removeItem(key));

    // Clear only supabase auth items from sessionStorage
    const sessionAuthKeys = Object.keys(sessionStorage).filter(key =>
      key.startsWith('supabase.auth.') ||
      key.startsWith('sb-') ||
      key.includes('-auth-token')
    );
    sessionAuthKeys.forEach(key => sessionStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing auth storage:', error);
  }
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);

  async function fetchProfile(userId: string): Promise<Profile | null> {
    try {
      // Get full profile data with all related information including past performance
      const { data: profile, error } = await supabase
        .from('profiles')
        .select(`
          *,
          pastPerformance:past_performance (
            id,
            project_name,
            sector_type,
            agency,
            agency_selection,
            contract_value,
            contract_type,
            deliverables
          )
        `)
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      return profile;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  }

  async function signIn(email: string, password: string, captchaToken: string) {
    try {
      const { data: { user: signedInUser }, error: signInError } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });

      if (signInError) throw signInError;
      if (!signedInUser) throw new Error('No user returned from sign in');

      setUser(signedInUser);

      const userProfile = await fetchProfile(signedInUser.id);
      if (!userProfile) {
        throw new Error('Unable to load user profile');
      }

      setProfile(userProfile);
      setAuthError(null);
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw new Error(error.message || 'Failed to sign in');
    }
  }

  async function signUp(email: string, password: string, companyName: string, role: Profile['role'], captchaToken: string) {
    try {
      const { data: { user: newUser }, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            company_name: companyName,
            role: role
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!newUser) throw new Error('No user returned from sign up');

      // Create profile
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([{ 
          id: newUser.id,
          company_name: companyName,
          role,
          membership_tier: 'free',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);

      if (profileError) throw profileError;

      const userProfile = await fetchProfile(newUser.id);
      if (!userProfile) {
        throw new Error('Failed to create user profile');
      }

      setUser(newUser);
      setProfile(userProfile);
      setAuthError(null);
    } catch (error: any) {
      console.error('Sign up error:', error);
      throw new Error(error.message || 'Failed to create account');
    }
  }

  async function signOut() {
    try {
      // First clear local state
      setUser(null);
      setProfile(null);
      setAuthError(null);

      // Clear only auth-related cached data (preserve app caches)
      clearAuthStorage();

      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Force reload the page to ensure clean state
      window.location.href = '/';
    } catch (error: any) {
      console.error('Sign out error:', error);
      // Even if there's an error, force sign out
      forceSignOut();
      throw new Error(error.message || 'Failed to sign out');
    }
  }

  const forceSignOut = () => {
    setUser(null);
    setProfile(null);
    setAuthError(null);
    clearAuthStorage();
    supabase.auth.signOut();
    window.location.href = '/';
  };

  async function updateProfile(updates: Partial<Profile>) {
    try {
      if (!user) throw new Error('No user logged in');

      // Check if any scoring-relevant fields are being updated
      const scoringFields = [
        'naics_codes',
        'qualified_set_asides',
        'core_competencies',
        'key_technologies',
        'industries_served',
        'value_proposition',
        'certifications'
      ];
      const affectsScoring = scoringFields.some(field => field in updates);

      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      // Invalidate cached scores if scoring-relevant fields changed
      // This ensures the dashboard will recalculate scores on next load
      if (affectsScoring) {
        await invalidateUserScores(user.id).catch(err => {
          console.warn('Failed to invalidate score cache:', err);
          // Don't throw - this is not critical
        });
      }

      const updatedProfile = await fetchProfile(user.id);
      if (updatedProfile) {
        setProfile(updatedProfile);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  useEffect(() => {
    let mounted = true;
    let authListener: any = null;

    async function initializeAuth() {
      try {
        setLoading(true);
        
        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          setAuthError(sessionError);
          forceSignOut();
          return;
        }

        if (session?.user) {
          setUser(session.user);
          const userProfile = await fetchProfile(session.user.id);
          if (mounted && userProfile) {
            setProfile(userProfile);
          }
        } else {
          // Clear any stale data if no session
          setUser(null);
          setProfile(null);
          clearAuthStorage();
        }

        // Set up auth state change listener
        authListener = supabase.auth.onAuthStateChange((event, session) => {
          if (!mounted) return;

          console.log('Auth state change:', event);

          if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
            setUser(null);
            setProfile(null);
            clearAuthStorage();
            window.location.href = '/';
            return;
          }

          if (session?.user) {
            setUser(session.user);
            // IMPORTANT: never await a Supabase call directly inside this
            // callback. supabase-js holds an internal auth lock while the
            // callback runs, so a DB query here can deadlock — which shows up
            // as the whole app hanging on "Loading..." until a page refresh
            // (notably after a token auto-refresh or when the tab regains
            // focus). Defer the fetch out of the callback, and only (re)fetch
            // the profile on an actual sign-in — not on every TOKEN_REFRESHED.
            if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
              setTimeout(async () => {
                if (!mounted) return;
                const userProfile = await fetchProfile(session.user.id);
                if (mounted && userProfile) {
                  setProfile(userProfile);
                }
              }, 0);
            }
          }
        });

      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthError(error instanceof Error ? error : new Error('Auth initialization failed'));
        if (mounted) {
          forceSignOut();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    initializeAuth();

    return () => {
      mounted = false;
      if (authListener) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    forceSignOut,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
export type { Profile };